/**
 * Metadata-only workbook bootstrap for native ReAct.
 *
 * Native mode starts with identity, selection, and sheet names. Used-range
 * shape is preserved only when the caller already has it; this module never
 * performs workbook I/O to manufacture missing shape. Cell values, formulas,
 * styles, tables, charts, and enrichment are deliberately excluded.
 */

import type { WorkbookContent } from "./types";

/** Maximum sheet metadata records placed in the initial native request. */
export const NATIVE_WORKBOOK_METADATA_MAX_SHEETS = 50;

/** Hard character ceiling for the serialized initial workbook metadata. */
export const NATIVE_WORKBOOK_METADATA_MAX_CHARS = 32_000;

const MAX_IDENTITY_CHARS = 512;
const MAX_SHEET_NAME_CHARS = 256;
const MAX_ADDRESS_CHARS = 256;

/** Used-range shape retained without any cell payload. */
export interface NativeWorkbookUsedRangeMetadata {
  address?: string;
  previewRowCount?: number;
  totalRowCount?: number;
  columnCount?: number;
}

/** One metadata-only worksheet inventory entry. */
export interface NativeWorkbookSheetMetadata {
  name: string;
  index?: number;
  visibility?: string;
  /** True only when `usedRange` contains host-provided shape metadata. */
  usedRangeMetadataAvailable: boolean;
  usedRange?: NativeWorkbookUsedRangeMetadata;
}

/** Bounded workbook context sent on the first native model turn. */
export interface NativeWorkbookBootstrap {
  inspectionMode: "metadata_only";
  metadataAvailable: boolean;
  instruction: string;
  workbookName?: string;
  activeSheetIndex?: number;
  activeSheetName?: string;
  activeCellAddress?: string;
  selectedRangeAddress?: string;
  isBusy?: boolean;
  isInEditMode?: boolean;
  sheets: NativeWorkbookSheetMetadata[];
  totalSheetCount: number;
  omittedSheetCount: number;
  sheetInventoryComplete: boolean;
  /** True only when every inventoried sheet has known used-range shape. */
  sheetShapeMetadataComplete: boolean;
}

/** Sources accepted by the native workbook bootstrap sanitizer. */
export interface NativeWorkbookBootstrapInput {
  /** Preferred structured snapshot already held by the caller. */
  workbookSnapshot?: WorkbookContent;
  /** Legacy serialized context, parsed only when no snapshot is available. */
  workbookContext?: string;
}

/**
 * Build a bounded metadata-only workbook context.
 *
 * @param input - Structured or serialized workbook context.
 * @returns Sanitized metadata with explicit inventory completeness.
 */
export function buildNativeWorkbookBootstrap(
  input: NativeWorkbookBootstrapInput,
): NativeWorkbookBootstrap {
  const source = resolveWorkbookSource(input);
  if (!source) return unavailableBootstrap();

  const rawSheets = Array.isArray(source.sheets) ? source.sheets : [];
  const totalSheetCount = rawSheets.length;
  const sheets = rawSheets
    .slice(0, NATIVE_WORKBOOK_METADATA_MAX_SHEETS)
    .map(toSheetMetadata)
    .filter((sheet): sheet is NativeWorkbookSheetMetadata => sheet !== null);
  const omittedSheetCount = Math.max(0, totalSheetCount - sheets.length);
  const sheetInventoryComplete = omittedSheetCount === 0;
  const sheetShapeMetadataComplete =
    sheetInventoryComplete &&
    sheets.every((sheet) => sheet.usedRangeMetadataAvailable);

  return {
    inspectionMode: "metadata_only",
    metadataAvailable: true,
    instruction: buildBootstrapInstruction(sheetShapeMetadataComplete),
    ...optionalTextField(
      "workbookName",
      source.workbookName,
      MAX_IDENTITY_CHARS,
    ),
    ...optionalNumberField("activeSheetIndex", source.activeSheetIndex),
    ...optionalTextField(
      "activeSheetName",
      source.activeSheetName,
      MAX_SHEET_NAME_CHARS,
    ),
    ...optionalTextField(
      "activeCellAddress",
      source.activeCellAddress,
      MAX_ADDRESS_CHARS,
    ),
    ...optionalTextField(
      "selectedRangeAddress",
      source.selectedRangeAddress,
      MAX_ADDRESS_CHARS,
    ),
    ...optionalBooleanField("isBusy", source.isBusy),
    ...optionalBooleanField("isInEditMode", source.isInEditMode),
    sheets,
    totalSheetCount,
    omittedSheetCount,
    sheetInventoryComplete,
    sheetShapeMetadataComplete,
  };
}

/**
 * Serialize metadata under a hard initial-request ceiling.
 *
 * If unusually long host metadata still crosses the ceiling after per-field
 * limits, trailing sheet records are removed and the omission count is updated
 * until the JSON is valid and bounded.
 *
 * @param input - Structured or serialized workbook context.
 * @returns Valid bounded JSON for the native request envelope.
 */
export function serializeNativeWorkbookBootstrap(
  input: NativeWorkbookBootstrapInput,
): string {
  const bootstrap = buildNativeWorkbookBootstrap(input);
  let serialized = JSON.stringify(bootstrap);
  while (
    serialized.length > NATIVE_WORKBOOK_METADATA_MAX_CHARS &&
    bootstrap.sheets.length > 0
  ) {
    bootstrap.sheets.pop();
    bootstrap.omittedSheetCount = Math.max(
      0,
      bootstrap.totalSheetCount - bootstrap.sheets.length,
    );
    bootstrap.sheetInventoryComplete = bootstrap.omittedSheetCount === 0;
    bootstrap.sheetShapeMetadataComplete =
      bootstrap.sheetInventoryComplete &&
      bootstrap.sheets.every(
        (sheet) => sheet.usedRangeMetadataAvailable,
      );
    bootstrap.instruction = buildBootstrapInstruction(
      bootstrap.sheetShapeMetadataComplete,
    );
    serialized = JSON.stringify(bootstrap);
  }
  return serialized.slice(0, NATIVE_WORKBOOK_METADATA_MAX_CHARS);
}

/**
 * Select the structured workbook source used by the metadata sanitizer.
 *
 * Structured snapshots take precedence. Legacy serialized context is accepted
 * only as a compatibility fallback and fails closed when malformed.
 *
 * @param input - Structured snapshot and optional legacy serialized context.
 * @returns Object-shaped workbook data, or `null` when no safe source exists.
 */
function resolveWorkbookSource(
  input: NativeWorkbookBootstrapInput,
): Record<string, unknown> | null {
  if (input.workbookSnapshot) {
    return input.workbookSnapshot as unknown as Record<string, unknown>;
  }
  if (!input.workbookContext?.trim()) return null;
  try {
    const parsed = JSON.parse(input.workbookContext) as unknown;
    return recordValue(parsed);
  } catch {
    return null;
  }
}

/**
 * Convert one untrusted worksheet record into bounded metadata.
 *
 * @param value - Unknown worksheet record from a snapshot or parsed context.
 * @returns Sanitized sheet metadata, or `null` when no valid name is present.
 */
function toSheetMetadata(value: unknown): NativeWorkbookSheetMetadata | null {
  const sheet = recordValue(value);
  if (!sheet) return null;
  const name = boundedText(sheet.name, MAX_SHEET_NAME_CHARS);
  if (!name) return null;
  const usedRange = recordValue(sheet.usedRange);
  const usedRangeMetadata = usedRange
    ? compactObject<NativeWorkbookUsedRangeMetadata>({
        ...optionalTextField(
          "address",
          usedRange.address,
          MAX_ADDRESS_CHARS,
        ),
        ...optionalNumberField("previewRowCount", usedRange.rowCount),
        ...optionalNumberField(
          "totalRowCount",
          usedRange.totalRowCount ?? usedRange.rowCount,
        ),
        ...optionalNumberField("columnCount", usedRange.columnCount),
      })
    : undefined;
  const usedRangeMetadataAvailable =
    Boolean(usedRangeMetadata) &&
    Object.keys(usedRangeMetadata ?? {}).length > 0;
  return {
    name,
    ...optionalNumberField("index", sheet.index),
    ...optionalTextField("visibility", sheet.visibility, 32),
    usedRangeMetadataAvailable,
    ...(usedRangeMetadataAvailable
      ? { usedRange: usedRangeMetadata }
      : {}),
  };
}

/**
 * Build the explicit fail-closed bootstrap used when inspection metadata is
 * unavailable.
 *
 * @returns Empty metadata that directs the model to use live targeted tools.
 */
function unavailableBootstrap(): NativeWorkbookBootstrap {
  return {
    inspectionMode: "metadata_only",
    metadataAvailable: false,
    instruction:
      "Workbook metadata was unavailable. Use list_sheets and targeted " +
      "read_range calls to establish the workbook purpose, relevant table grain, " +
      "and task scope; do not infer workbook content.",
    sheets: [],
    totalSheetCount: 0,
    omittedSheetCount: 0,
    sheetInventoryComplete: false,
    sheetShapeMetadataComplete: false,
  };
}

/**
 * Describe precisely what the bootstrap knows without encouraging an eager
 * workbook scan.
 *
 * @param sheetShapeMetadataComplete - Whether every inventoried sheet already
 *   carried usable used-range shape.
 * @returns Model-facing instruction distinguishing unknown shape from blank.
 */
function buildBootstrapInstruction(
  sheetShapeMetadataComplete: boolean,
): string {
  const base =
    "This bootstrap contains no cell values or formulas. Use live reads to " +
    "establish the workbook purpose, relevant table grain, and task scope. ";
  return sheetShapeMetadataComplete
    ? `${base}Use targeted read_range calls only when the request requires workbook content.`
    : `${base}Missing used-range metadata means unknown, not blank. Use list_sheets before targeted read_range calls that require workbook shape.`;
}

/**
 * Narrow an unknown value to a non-array object record.
 *
 * @param value - Unknown value to inspect.
 * @returns The object record, or `null` for primitives, arrays, and null.
 */
function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Normalize and bound an optional text field.
 *
 * @param value - Unknown candidate value.
 * @param maxChars - Maximum retained character count.
 * @returns Trimmed bounded text, or `undefined` when no usable text exists.
 */
function boundedText(value: unknown, maxChars: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (!text) return undefined;
  return text.slice(0, maxChars);
}

/**
 * Construct an optional bounded string property without introducing
 * `undefined` into serialized metadata.
 *
 * @param key - Property name to emit.
 * @param value - Unknown candidate value.
 * @param maxChars - Maximum retained character count.
 * @returns A one-property object when valid, otherwise an empty object.
 */
function optionalTextField<Key extends string>(
  key: Key,
  value: unknown,
  maxChars: number,
): Partial<Record<Key, string>> {
  const text = boundedText(value, maxChars);
  return text ? ({ [key]: text } as Record<Key, string>) : {};
}

/**
 * Construct an optional finite numeric property.
 *
 * @param key - Property name to emit.
 * @param value - Unknown candidate value.
 * @returns A one-property object when finite, otherwise an empty object.
 */
function optionalNumberField<Key extends string>(
  key: Key,
  value: unknown,
): Partial<Record<Key, number>> {
  return typeof value === "number" && Number.isFinite(value)
    ? ({ [key]: value } as Record<Key, number>)
    : {};
}

/**
 * Construct an optional boolean property.
 *
 * @param key - Property name to emit.
 * @param value - Unknown candidate value.
 * @returns A one-property object for booleans, otherwise an empty object.
 */
function optionalBooleanField<Key extends string>(
  key: Key,
  value: unknown,
): Partial<Record<Key, boolean>> {
  return typeof value === "boolean"
    ? ({ [key]: value } as Record<Key, boolean>)
    : {};
}

/**
 * Remove undefined fields from a metadata object while retaining its static
 * TypeScript shape.
 *
 * @param value - Object containing optional metadata fields.
 * @returns A shallow object containing only defined entries.
 */
function compactObject<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined),
  ) as T;
}
